import React, {
    Suspense,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { RouteComponentProps } from 'react-router-dom';
import './dyf-landing-view.scss';
import ServerContext from '../../contexts/serverContext';
import { useDyfFeatureGuideBreadcrumbsContent } from './hooks/use-dyf-feature-guide-breadcrumbs-content';
import { useFeatureGuideContent } from './hooks/use-dyf-feature-guide-content';
import { useDyfEligibleModel } from '../../hooks/dyf/use-dyf-eligible-model/use-dyf-eligible-model';
// import { ViewTemplate } from '../../components/sections/view-template/view-template';
import { VehicleAttributes } from '../../models/vehicle-attributes';
import useSeoConfig from '../../hooks/use-seo-config';
import useUserProfile from '../../hooks/use-user-profile';
import { useSearchContent } from '../../components/sections/search-bar/hooks/use-search-content';
import { useAnalytics } from '../../hooks/use-analytics';
import { findByAlias } from '../../routes';
// import { FeatureRibbonDisclaimerFooter } from '../../components/sections/onboarding-global-components/onboarding-feature-ribbon/feature-ribbon-disclaimer-footer/feature-ribbon-disclaimer-footer';
import { DyfLandingViewFord } from './dyf-landing-view.ford';
import { DyfLandingViewLincoln } from './dyf-landing-view.lincoln';
import {
    AuthenticationState,
    useAuthentication,
} from '../../hooks/use-authentication';
import { VehicleYmm } from '../../models/vehicle-ymm';
import { useValidVin } from './hooks/use-valid-vin';
import { useDyfVehicleYmm } from './hooks/use-dyf-vehicle-ymm';
import { useDyfQueryParams } from '../../hooks/dyf/use-dyf-query-params/use-dyf-query-params';
import { useFeatureGuideDaosAnalytics } from '../../hooks/dyf/use-feature-guide-daos-analytics/use-feature-guide-daos-analytics';
// import { PreImagineEscapeHatch } from '../../components/sections/pre-imagine-components/pre-imagine-escape-hatch/pre-imagine-escape-hatch';
// import {useVehicleImage} from "../../hooks/use-vehicle-image";
import { useWindowDevice } from '../../hooks/use-window-device/use-window-device';

const FeatureRibbonDisclaimerFooter = React.lazy(() =>
    import(
        '../../components/sections/onboarding-global-components/onboarding-feature-ribbon/feature-ribbon-disclaimer-footer/feature-ribbon-disclaimer-footer'
    ).then(module => ({
        default: module.FeatureRibbonDisclaimerFooter,
    }))
);

type DYFFeatureGuideLandingFlow = 'ymm' | 'vin';
export type DYFFeatureGuideLandingPageProps = RouteComponentProps<
    any,
    any,
    any
> & {
    flow: DYFFeatureGuideLandingFlow;
};

export const DyfLandingView = (props: DYFFeatureGuideLandingPageProps) => {
    const {
        queryYear = '',
        queryModel = '',
        queryVin = '',
    } = useDyfQueryParams();

    const { brand, currentLanguageRegionCode, root } = useContext(
        ServerContext
    );

    const eligibleQuery = useMemo(
        () =>
            props.flow == 'vin'
                ? { vin: queryVin }
                : { modelAlias: queryModel, year: queryYear },
        [queryVin]
    );

    const {
        data: eligibleModelData,
        loading: eligibleModelDataLoading,
        executeQuery: executeEligibleModelQuery,
    } = useDyfEligibleModel(eligibleQuery);

    const featureGuideContent = useFeatureGuideContent();
    const dyfLandingBreadcrumbs = useDyfFeatureGuideBreadcrumbsContent();

    const windowDevice = useWindowDevice();
    const isMobile = windowDevice === 'mobile';

    /*const { vehicleImage } = useVehicleImage({
        year: Number(eligibleModelData?.modelYear) || 0,
        vin: queryVin!,
    });*/

    useValidVin();

    const profile = useUserProfile();

    const [authState] = useAuthentication();
    const {
        data: vehicleYmmGroup,
        loading: vehicleYmmGroupLoading,
    } = useDyfVehicleYmm(props.flow);

    const [dyfLoadAnalyticFired, setDyfLoadAnalyticFired] = useState<boolean>(
        false
    );

    const userGuid = useMemo(() => profile?.profile?.userGuid || '', [
        profile?.profile,
    ]);
    const isAuthenticated = useMemo(() => {
        return authState === AuthenticationState.Authenticated;
    }, [authState]);

    const baseRoot = useMemo(
        () => (root ? root.substring(0, root.length - 1) : ''),
        [root]
    );

    useEffect(() => {
        if (
            vehicleYmmGroup?.make === '' ||
            vehicleYmmGroupLoading ||
            !vehicleYmmGroup
        ) {
            return;
        } else if (!dyfLoadAnalyticFired)
            handleFeatureGuideAnalyticsDyf(vehicleYmmGroup);
    }, [vehicleYmmGroup, dyfLoadAnalyticFired, vehicleYmmGroupLoading]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }

        if (!eligibleModelDataLoading && eligibleModelData == null) {
            const redirectPath = props.location.pathname.replace(
                /\/discover-your-(ford|lincoln)/i,
                ''
            );
            props.history.push(redirectPath || '/');
            return;
        }
        /*if (
            featureGuideContent &&
            !contentLoaded &&
            !eligibleModelDataLoading
        ) {
            if (brand === 'lincoln' || (searchContent && helpCardsContent)) {
                setContentLoaded(true);
            }
        }*/
    }, [
        featureGuideContent,
        eligibleModelDataLoading,
        changeFeatureGuide,
        props.history,
        props.location.pathname,
        // contentLoaded,
        // searchContent,
        // helpCardsContent,
    ]);

    const searchContent = useSearchContent(undefined, true);
    const [fireEvents] = useAnalytics();

    function handleFeatureGuideAnalyticsDyf(ymm: VehicleYmm) {
        const getEventName = () => {
            if (props.flow == 'vin') {
                if (eligibleModelData) {
                    return eligibleModelData.eligibleFeatureGuide
                        ? 'dyf-feature-guide-landingpage-vin'
                        : 'dyf-landingpage-vinnonmvp';
                } else {
                    return 'dyf-feature-guide-landingpage-vin-nondyf';
                }
            } else {
                return 'dyf-feature-guide-landingpage-ymm';
            }
        };

        const eventName = getEventName();

        fireEvents(eventName, undefined, {
            ymm,
            dyfComponentName: 'Feature guide',
        });
        setDyfLoadAnalyticFired(true);
    }

    function changeFeatureGuide(vin: string) {
        const newVinPath = findByAlias('DyfLandingView')[0].replace(
            ':vin',
            vin
        );

        if (newVinPath) {
            props.history.replace({
                pathname: newVinPath,
            });
        }
    }

    useEffect(() => {
        if (queryVin) executeEligibleModelQuery({ vin: queryVin });
    }, [queryVin]);

    function handleVehicleSelectorOnChange(
        title: string,
        index: number,
        value: string,
        match: VehicleAttributes
    ) {
        if (match?.vin) {
            changeFeatureGuide(match.vin);
        }
    }

    const shouldShowFeatureRibbon = useMemo<boolean>(() => {
        if (props.flow == 'vin') {
            return true;
        }
        return false;
    }, [props.flow]);

    const shouldShowOwnersManual5050 = useMemo(() => {
        if (featureGuideContent && vehicleYmmGroup?.year) return true;
        else return false;
    }, [featureGuideContent, vehicleYmmGroup]);

    const shouldShowYourVehicleFeatures = useMemo(() => {
        return !!eligibleModelData?.eligibleFeatureGuide;
    }, [eligibleModelData]);

    const shouldShowSearchBar = useMemo(() => {
        return (
            searchContent &&
            eligibleModelData?.eligibleFeatureGuide &&
            props.flow === 'vin'
        );
    }, [searchContent, eligibleModelData?.eligibleFeatureGuide]);

    const shouldShowFeatureGuideCards = useMemo(() => !!eligibleModelData, [
        eligibleModelData,
    ]);

    useSeoConfig('dyf-landing', 'dyf', []);
    useFeatureGuideDaosAnalytics();

    return (
        <div className="dyf-landing-page" data-testid="dyf-landing-page">
            {brand === 'ford' && (
                <DyfLandingViewFord
                    shouldShowVehicleSelector={
                        isAuthenticated && !!vehicleYmmGroup
                    }
                    fireEvents={fireEvents}
                    vehicleYmmGroup={vehicleYmmGroup}
                    featureGuideContent={featureGuideContent}
                    eligibleModelData={eligibleModelData}
                    handleVehicleSelectorOnChange={
                        handleVehicleSelectorOnChange
                    }
                    vin={queryVin || null}
                    shouldShowFeatureRibbon={shouldShowFeatureRibbon}
                    shouldShowOwnersManual5050={
                        shouldShowOwnersManual5050 ?? false
                    }
                    searchContent={searchContent}
                    baseRoot={baseRoot}
                    currentLanguageRegionCode={currentLanguageRegionCode}
                    vehicleAttributes={vehicleYmmGroup}
                    userGuid={userGuid}
                    profile={profile}
                    showYourVehicleFeatures={shouldShowYourVehicleFeatures}
                    flow={props.flow}
                    shouldShowSearchBar={!!shouldShowSearchBar}
                    dyfLandingBreadcrumbs={dyfLandingBreadcrumbs}
                    shouldShowFeatureGuideCards={shouldShowFeatureGuideCards}
                    // vehicleImage={vehicleImage}
                    vehicleImage={''}
                    isMobile={isMobile}
                />
            )}
            {brand === 'lincoln' && vehicleYmmGroup && (
                <DyfLandingViewLincoln
                    dyfLandingBreadcrumbs={dyfLandingBreadcrumbs}
                    fireEvents={fireEvents}
                    vehicleAttributes={vehicleYmmGroup}
                    handleVehicleSelectorOnChange={
                        handleVehicleSelectorOnChange
                    }
                    featureGuideContent={featureGuideContent}
                    eligibleModelData={eligibleModelData}
                    vin={queryVin}
                    isAuthenticated={isAuthenticated}
                    baseRoot={baseRoot}
                    vehicleYmmGroup={vehicleYmmGroup}
                    flow={props.flow}
                />
            )}
            <Suspense>
                {eligibleModelData && shouldShowFeatureRibbon && (
                    <FeatureRibbonDisclaimerFooter
                        eligibleTowingCalculator={
                            eligibleModelData.eligibleTowingCalculator || false
                        }
                        vin={queryVin}
                        vehicleType={eligibleModelData.vehicleType}
                    />
                )}
            </Suspense>
        </div>
    );
};
export default DyfLandingView;
